import { createMachine, assign, Interpreter, AnyEventObject, State, Typestate } from "xstate";
import { useMachine } from "@xstate/react";
import { BaseConfigType, CredentialType } from "./types";
import { LegacyMuninInterface } from "./interfaces/LegacyMuninInterface";
import { Platform, AsyncStorage } from "./native";

/** The base XState context (not React!) */
export interface BaseContextType extends BaseConfigType, CredentialType {
  /** Try to get a unique identifier for the hardware */
  hardwareFingerprint?: string;
}

export const UWP_CONFIG_FILENAME = "config.json";
export const UWP_CREDENTIAL_RESOURCE = "source";

const getUwpConfigFileContents = () =>
  // eslint-disable-next-line no-undef
  Windows.Storage.ApplicationData.current.localFolder
    .createFileAsync(
      UWP_CONFIG_FILENAME,
      // eslint-disable-next-line no-undef
      Windows.Storage.CreationCollisionOption.openIfExists
    )
    .then((file: unknown) =>
      // eslint-disable-next-line no-undef
      Windows.Storage.FileIO.readTextAsync(file).then((fileContents: string) => JSON.parse(fileContents))
    );

const getUwpPasswordCredentials = (context: BaseContextType) =>
  new Promise<CredentialType>((resolve, reject) => {
    console.debug("Looking for credentials");
    if (!context.username) {
      console.log("No username");
      reject();
    }
    // eslint-disable-next-line no-undef
    const passwordVault = Windows.Security.Credentials.PasswordVault();
    const credential: { password?: string } | undefined = passwordVault.retrieve(
      UWP_CREDENTIAL_RESOURCE,
      context.username
    );
    if (context.username && credential?.password) {
      // TODO: Test that the existing credentials work?
      resolve({ password: credential.password });
    }
  });

const getIdentityFromLegacyMuninInterface = (context: BaseContextType) =>
  new Promise<BaseContextType>((resolve, reject) => {
    console.debug("Looking for credentials");
    const legacyMuninInterface = new LegacyMuninInterface((message) => {
      // Filter just the "Identity" messages that may come across. There are
      // a bunch of different types of messages we may see.
      if (message.sinkType === "Identity" && message.payload) {
        const identity = message.payload as BaseContextType;
        resolve({ ...identity, password: identity.password && atob(identity.password) });
      }
    });
    // Wait just a little bit for the interface to be up. This may just be
    // necessary for the mock interface. If there is no successful identity
    // in five seconds, fail (and handle failure/retry in the state machine)
    // TODO: Get rid of this delay?
    window.setTimeout(() => legacyMuninInterface.requestIdentity(), 1000);
    window.setTimeout(() => reject(), 5000);
  });

const getIdentityFromIosInterface = async () => {
  const config = await AsyncStorage.getItem<BaseContextType>("config");

  if (!config) {
    throw new Error("Missing config.");
  }

  return config;
};

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export const provisioningMachine = createMachine<BaseContextType>({
  id: "provisioning-machine",
  initial: "init",
  context: {} as BaseContextType,
  states: {
    // Start in the init state and immediately test the various conditions to
    // identify the provisioning environment. Default to provisioning as a
    // Progressive Web App (PWA).
    init: {
      // Immediately take the first transition that satisfies the
      // conditional guard
      always: [
        { target: "uwp", cond: () => Platform.OS === "windows" },
        { target: "legacyMunin", cond: () => Platform.OS === "android" },
        { target: "ios", cond: () => Platform.OS === "ios" },
        { target: "pwa" }, // Default to PWA (no conditions for transition)
      ],
    },

    // Handle provisioning when running in a Universal Windows Platform (UWP)
    // app, e.g., Sense UWP running on Windows (or an Xbox or HoloLens!)
    uwp: {
      initial: "getFile",
      states: {
        getFile: {
          invoke: {
            id: "getConfigFileUwp",
            src: (): Promise<BaseConfigType> => getUwpConfigFileContents(),
            onDone: {
              target: "getCredentials",
              actions: assign((context, event) => event.data),
            },
            onError: {
              target: "setFile",
            },
          },
        },
        setFile: {
          on: {
            DONE: "getFile",
          },
        },
        getCredentials: {
          invoke: {
            id: "getPasswordCredentials",
            src: (context: BaseContextType): Promise<CredentialType> => getUwpPasswordCredentials(context),
            onDone: {
              target: "success",
              actions: assign({
                // For UWP the password is not in the config file, it is
                // stored in an encrypted credential store that must be
                // accessed with a separate call. Here we handle the password
                // setting in the context, everything else should already be
                // there.
                password: (context, event) => event.data.password,
              }),
            },
            onError: {
              target: "setCredentials",
            },
          },
        },
        setCredentials: {
          on: {
            DONE: "getCredentials",
          },
        },
        success: {
          type: "final",
        },
      },
    },

    // TODO: Actually implement the legacy Munin provisioning
    // Handle provisioning when running in a legacy Munin environment. Here we
    // look for the legacy Munin interface in order to get the identity of the
    // hublet, including the hublet credentials. In order to do that we
    // construct an instance of the interface, send a "get identity" message,
    // then await the identity response. If we don't get the identity response
    // in a timely manner, then try again.
    legacyMunin: {
      initial: "getIdentity",
      states: {
        getIdentity: {
          invoke: {
            id: "getIdentityFromLegacyMuninInterface",
            src: (context: BaseContextType): Promise<BaseContextType> =>
              getIdentityFromLegacyMuninInterface(context),
            onDone: {
              target: "success",
              actions: assign((context, event) => event.data),
            },
            onError: {
              target: "waitAndRetry",
            },
          },
        },
        waitAndRetry: {
          after: { 5000: "getIdentity" },
        },
        success: {
          // TODO: Something like: actions: getContextThroughLegacyMuninInterface,
          type: "final",
        },
      },
    },

    // TODO: Next-generation Android app

    // Handle provisioning when running in a native iOS app (e.g., on iPad)
    ios: {
      initial: "getFile",
      states: {
        getFile: {
          invoke: {
            id: "getConfigFileIos",
            src: (): Promise<BaseConfigType> => getIdentityFromIosInterface(),
            onDone: {
              target: "success",
              actions: assign((context, event) => event.data),
            },
            onError: {
              target: "setFile",
            },
          },
        },
        setFile: {
          on: {
            DONE: "getFile",
          },
        },
        success: {
          type: "final",
        },
      },
    },

    // Handle provisioning when running as a Progressive Web App (PWA). This,
    // for now, means prompting the user for credentials, but that logic is
    // handled by the <Provisioning/> component rather than xstate.
    pwa: {
      initial: "success",
      states: {
        success: {
          type: "final",
        },
      },
    },
  },
});

export const useProvisioningMachine = (): [
  State<BaseContextType, AnyEventObject, any, Typestate<BaseContextType>>,
  Interpreter<BaseContextType, any, AnyEventObject, Typestate<BaseContextType>>["send"],
  Interpreter<BaseContextType, any, AnyEventObject, Typestate<BaseContextType>>,
] =>
  useMachine(
    provisioningMachine
  ) as any; /* https://github.com/pmndrs/jotai/issues/1000#issuecomment-1032882722 */
